import { COMMIT_HASH } from "@app/lib/commit-hash";
import { clientEventSource } from "@app/lib/egress/client";
import {
  EventSourcePolyfill,
  type Event as PolyfillEvent,
  type MessageEvent as PolyfillMessageEvent,
} from "event-source-polyfill";
import { useCallback, useEffect, useRef, useState } from "react";

const RECONNECT_DELAY_BASE_MS = 3000; // 3 seconds base delay
const RECONNECT_DELAY_JITTER_MS = 5000; // +0-5 seconds random jitter
const MAX_RECONNECT_ATTEMPTS = 10;
// Server-side SSE endpoints can be idle up to 3 minutes (conversation events)
// before sending "done". The polyfill's default heartbeat timeout is 45s which
// would cause spurious reconnects during normal idle periods.
const HEARTBEAT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Stable EventSource Manager
 *
 * This singleton object manages EventSource instances across the entire application.
 * It provides methods to create, retrieve, and remove EventSource connections,
 * ensuring that each unique connection is properly managed and can be accessed
 * or closed as needed.
 *
 * Key features:
 * - Maintains a single source of truth for all EventSource instances.
 * - Prevents duplicate EventSource creations for the same unique identifier.
 * - Allows for centralized management of EventSource lifecycle.
 * - Improves performance by reusing existing connections when possible.
 *
 * Usage:
 * - Create a new EventSource: stableEventSourceManager.create(url, uniqueId)
 * - Retrieve an existing EventSource: stableEventSourceManager.get(uniqueId)
 * - Close and remove an EventSource: stableEventSourceManager.remove(uniqueId)
 *
 * This manager is designed to be used in conjunction with the useEventSource hook
 * to provide stable and efficient EventSource handling across React component lifecycles.
 */
const stableEventSourceManager = {
  // Map to store active EventSource instances, keyed by unique identifiers
  sources: new Map<string, EventSourcePolyfill>(),

  /**
   * Creates a new EventSource instance and stores it in the sources map.
   * @param url The URL to connect the EventSource to
   * @param uniqueId A unique identifier for this EventSource instance
   * @returns The newly created EventSource instance
   */
  create(url: string, uniqueId: string) {
    const urlWithCommitHash = new URL(url, document.baseURI);
    urlWithCommitHash.searchParams.append("commitHash", COMMIT_HASH);

    // Build a relative path so clientEventSource can rewrite and authenticate it.
    const pathWithQueryAndHash =
      urlWithCommitHash.pathname +
      urlWithCommitHash.search +
      urlWithCommitHash.hash;

    const newSource = clientEventSource(pathWithQueryAndHash, {
      heartbeatTimeout: HEARTBEAT_TIMEOUT_MS,
    });
    this.sources.set(uniqueId, newSource);

    return newSource;
  },

  /**
   * Retrieves an existing EventSource instance by its unique identifier.
   * @param uniqueId The unique identifier of the EventSource to retrieve
   * @returns The EventSource instance if found, undefined otherwise
   */
  get(uniqueId: string) {
    return this.sources.get(uniqueId);
  },

  /**
   * Closes and removes an EventSource instance from the sources map.
   * @param uniqueId The unique identifier of the EventSource to remove
   */
  remove(uniqueId: string) {
    const source = this.sources.get(uniqueId);
    if (source) {
      source.close();
      this.sources.delete(uniqueId);
    }
  },
};

export function useEventSource(
  buildURL: (lastEvent: string | null) => string | null,
  onEventCallback: (event: string) => void,
  uniqueId: string,
  { isReadyToConsumeStream = true }: { isReadyToConsumeStream?: boolean } = {}
) {
  const [isError, setIsError] = useState<Error | null>(null);
  const lastEvent = useRef<string | null>(null);
  const reconnectAttempts = useRef(0);

  // We use a counter to trigger reconnects when the counter changes.
  const [reconnectCounter, setReconnectCounter] = useState(0);

  // Store the reconnect timeout reference to clear it when needed.
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use the stable event source manager to ensure consistent EventSource management
  // across renders and component lifecycles.
  const sourceManager = stableEventSourceManager;

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  const connect = useCallback(() => {
    const url = buildURL(lastEvent.current);
    if (!url) {
      // If the url is empty, it means streaming is done.
      // Close any previous connections for this uniqueId and remove it from the manager.
      sourceManager.remove(uniqueId);

      return null;
    }

    let source = sourceManager.get(uniqueId);
    // If the source is closed or doesn't exist, create a new one.
    if (!source || source.readyState === EventSourcePolyfill.CLOSED) {
      source = sourceManager.create(url, uniqueId);
    }

    source.onopen = () => {
      // If connected, reset the reconnect attempts and clear the reconnect timeout.
      reconnectAttempts.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };

    source.onmessage = (event: PolyfillMessageEvent) => {
      if (event.data === "done") {
        source.close();

        // Reconnect to the stream right away.
        setReconnectCounter((c) => c + 1);
        return;
      }

      onEventCallback(event.data);
      lastEvent.current = event.data;
    };

    source.onerror = (event: PolyfillEvent) => {
      console.error("EventSource error", event);
      source.close();

      reconnectAttempts.current++;

      if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
        console.log(
          "Too many errors, not reconnecting. Please refresh the page."
        );
        setIsError(new Error("Too many errors, closing connection."));

        return;
      }

      // Add jitter to prevent synchronized reconnection bursts during deployment.
      const reconnectDelayMs =
        RECONNECT_DELAY_BASE_MS + Math.random() * RECONNECT_DELAY_JITTER_MS;

      console.error(
        `Connection error. Attempting to reconnect in ${Math.round(reconnectDelayMs)}ms`
      );

      // Set timeout to reconnect after a jittered delay.
      reconnectTimeoutRef.current = setTimeout(() => {
        setReconnectCounter((c) => c + 1);
      }, reconnectDelayMs);
    };

    return source;
  }, [buildURL, onEventCallback, uniqueId, sourceManager]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  useEffect(() => {
    if (!isReadyToConsumeStream || isError) {
      return;
    }

    connect();

    return () => {
      sourceManager.remove(uniqueId);

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [
    isReadyToConsumeStream,
    connect,
    reconnectCounter,
    sourceManager,
    uniqueId,
    isError,
  ]);

  return { isError };
}
